import React from 'react'
import PropTypes from 'prop-types'
import { Box, Flex, Badge, Stack, Container } from '@chakra-ui/react'
import { withTranslation } from 'i18n'
import { dateFormatLocale } from 'lib/date'
import { loadImage } from 'integrations/unsplash'
import truncate from 'lodash/truncate'
import HTMLWrapper from 'lib/html-wrapper'

const ProjectLayoutEight = ({ t, data, meta, theme, isInsideAdminView, i18n }) => {
  if (!data.length && !isInsideAdminView) {
    return false
  }

  const sectionTitleNode = () => {
    if (!meta.title) return false

    return (
      <Box
        as='h2'
        color={theme.sectionTitleColor}
        fontSize='sectionTitle'
        fontWeight='heading'
        fontFamily='heading'
        mb={4}
      >
        {meta.title}
      </Box>
    )
  }

  const statusNode = (status) => {
    if (meta.autoAddProjects && meta.showProjectStatus) {
      return (
        <Badge
          fontFamily='body'
          fontSize='xs'
          rounded='site'
          variant='solid'
          fontWeight='normal'
          position='absolute'
          top={4}
          right={4}
          backgroundColor='gray.700'
        >
          {status}
        </Badge>
      )
    }

    return false
  }

  const CTAButtonNode = () => {
    if (!meta.ctaText) return false

    return (
      <Box
        alignItems='center'
        bg={theme.ctaBackgroundColor || 'brand'}
        boxShadow='site'
        color={theme.ctaTextColor || 'white'}
        _hover={{
          bg: theme.ctaBackgroundHoverColor || theme.ctaBackgroundColor || 'brand',
          color: theme.ctaTextHoverColor || theme.ctaTextColor || '#fff'
        }}
        d='inline-flex'
        fontFamily='body'
        fontSize='body'
        fontWeight='bold'
        justifyContent='center'
        mt={4}
        px={2}
        py={1}
        rounded='site'
        textDecoration='none'
        className='project-button'
      >
        <Box>{truncate(meta.ctaText, { length: 20 })}</Box>
      </Box>
    )
  }

  const descriptionNode = (description) => {
    return (
      <Box
        color={theme.textColor}
        fontFamily='body'
        fontWeight='body'
        fontSize='body'
        wordBreak='break-word'
      >
        <HTMLWrapper html={description} allowTags={['br', '&', 'p']} truncateLength={100} />
      </Box>
    )
  }

  const titleNode = (title) => {
    return (
      <Box
        color={theme.textColor}
        fontFamily='heading'
        fontWeight='heading'
        fontSize='heading'
        wordBreak='break-word'
        as='h3'
      >
        {truncate(title, { length: 70 })}
      </Box>
    )
  }

  const publishDateNode = (date, ctaLinkType) => {
    if (!meta.showPublishDate || !date || (ctaLinkType !== 'projects' && ctaLinkType !== undefined)) return false

    return (
      <Box
        color='gray.700'
        fontFamily='body'
        fontSize='body'
        fontWeight='body'
        mt={1}
      >
        {dateFormatLocale(date, i18n.language)}
      </Box>
    )
  }

  const cardsListNode = (projects) => {
    return (
      <Flex
        flexWrap='wrap'
        flexDirection={{xs: 'column', md: 'row' }}
        mx={-6}
      >
        {projects.map((project, index) => {
          return (
            <Box
              key={index}
              px={6}
              py={6}
              w={['100%', '50%', '50%', '33.33%']}
            >
              <Box
                as='a'
                aria-label={`${i18n.t('editor:editor.viewProject')} - ${project.title || project.name}`}
                bg={theme.cardBackgroundColor || '#ffffff'}
                boxShadow='site'
                href={meta.autoAddProjects ? `/${project.permalink}` : project.permalink}
                target={project.openInNewTab ? '_blank' : '_self'}
                overflow='hidden'
                pos='relative'
                rounded='site'
                d='flex'
                flexDirection='column'
                h='full'
                className='animated-card'
              >
                <Box
                  minHeight={48}
                  width='100%'
                  overflow='hidden'
                >
                  <Box
                    backgroundColor={!project.image ? 'brand' : null}
                    backgroundImage={`url(${loadImage(project.image || project.imageUrl || project.bannerUrl, meta.imageSize || 800)})`}
                    backgroundPosition={`${project.imgFocalX}% ${project.imgFocalY}%`}
                    backgroundRepeat='no-repeat'
                    backgroundSize='cover'
                    minHeight={48}
                    w='full'
                    className='animated-card-image'
                    role='img'
                    aria-label=''
                  />
                </Box>
                {statusNode(project.state)}
                <Box
                  display='flex'
                  flexDirection='column'
                  justifyContent='space-between'
                  p={6}
                  h='full'
                >
                  <Box>
                    <Stack spacing={3}>
                      <Box>{publishDateNode(project.publishedAt, project.ctaLinkType)}</Box>
                      <Box>
                        {titleNode(project.title || project.name)}
                      </Box>
                      <Box>{descriptionNode(project.description)}</Box>
                    </Stack>
                  </Box>
                  <Box>
                    {CTAButtonNode()}
                  </Box>
                </Box>
              </Box>
            </Box>
          )
        })}
      </Flex>
    )
  }

  return (
    <Box
      bg={theme.backgroundColor}
      px={[2, 4, 8, 16]}
    >
      <Box bg={theme.backgroundColor} py={8} px={[4, 4, 0, 0]}>
        <Container maxW='container.lg' mx='auto' px={0}>
          {sectionTitleNode()}
          {cardsListNode(data)}
        </Container>
      </Box>
    </Box>
  )
}

ProjectLayoutEight.propTypes = {
  data: PropTypes.array,
  meta: PropTypes.object,
  theme: PropTypes.object,
  isInsideAdminView: PropTypes.bool,
  t: PropTypes.func.isRequired,
  i18n: PropTypes.object.isRequired
}

export default withTranslation(['editor'])(ProjectLayoutEight)
